/**
 * CountryFlag — render an ISO 3166-1 alpha-2 country flag as a bundled SVG.
 *
 * DEV-1488. Replaces the regional-indicator emoji approach (see the old
 * `countryFlag()` helpers). Emoji flags render as flags only on macOS/iOS;
 * Windows and older Android paint the two ISO letters instead, which is what
 * surfaced as stray "initials" on avatars for viewers not on a Mac. This
 * component draws the flag from the locally bundled `flag-icons` SVG set —
 * consistent on every platform, and no external CDN (so no per-viewer
 * location signal leaks to a third party).
 *
 * Contract: FLAG OR NOTHING. Given no code, an invalid code, or a code
 * `flag-icons` does not carry, it renders nothing (returns null). It never
 * falls back to letters or an empty box.
 *
 * flag-icons exposes `.fi` (base: 4x3 background sizing off font-size) plus
 * `.fi-<cc>` (the specific flag). The wrapper sets font-size so the flag
 * scales; border-radius clips the background to a rounded rectangle.
 */
import React from 'react';

/** ISO codes flag-icons does not ship as a plain 2-letter file. Guarded so we
 *  render nothing rather than an empty box. `uk` is the classic trap — the
 *  United Kingdom is `gb`, not `uk`. */
const ALIAS: Record<string, string> = { uk: 'gb' };

function normalizeCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const upper = raw.toUpperCase().trim();
  // Accept "US" or a "US-MA" style prefix; take the leading country segment.
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : upper.length === 2 ? upper : '';
  if (!/^[A-Z]{2}$/.test(cc)) return null;
  const lower = cc.toLowerCase();
  return ALIAS[lower] ?? lower;
}

export interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 code (case-insensitive). */
  code: string | null | undefined;
  /** Rendered flag width in px (height derives from the 4:3 ratio). Default 20. */
  size?: number;
  /** Corner radius in px. Default 3. */
  radius?: number;
  /** Optional drop shadow for placement over imagery (e.g. avatar badge). */
  shadow?: boolean;
  /** Accessible label; defaults to the country code. */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

/** A single country flag, or nothing when there is no valid flag to show. */
export const CountryFlag: React.FC<CountryFlagProps> = ({
  code,
  size = 20,
  radius = 3,
  shadow = false,
  title,
  className,
  style,
}) => {
  const cc = normalizeCode(code);
  if (!cc) return null;

  // flag-icons sizes off font-size: width = 1.333em, height = 1em. Setting
  // fontSize = size/1.333 makes the rendered WIDTH equal `size`px.
  const fontSize = size / (4 / 3);

  return (
    <span
      className={`fi fi-${cc}${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={title ?? cc.toUpperCase()}
      title={title ?? cc.toUpperCase()}
      style={{
        fontSize,
        borderRadius: radius,
        // .fi is inline-block already; keep it crisp over busy backgrounds.
        boxShadow: shadow ? '0 1px 4px rgba(0,0,0,0.55)' : undefined,
        verticalAlign: 'middle',
        ...style,
      }}
    />
  );
};

export default CountryFlag;
